"use strict";
/**
 * Sport-generic per-button pricing engine for combined Sport + Manual Price
 * mode: button metadata contract, price steppers, the shared price modal,
 * pin (set-to-ask) behavior, and hint text.
 *
 * Sport modules (baseball/, basketball/) supply `SportButtonMeta[]` tables;
 * all prices are stored in `tradingSettings.baseballButtonManualPrices`
 * (shared across sports — keys are sport-scoped, e.g. "single_a" vs
 * "bk_2ptr_a").
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAskPrice = getAskPrice;
exports.getSportButtonHintText = getSportButtonHintText;
exports.refreshSportButtonHints = refreshSportButtonHints;
exports.setupSportPriceSteppers = setupSportPriceSteppers;
exports.getSportPinnedPrice = getSportPinnedPrice;
exports.setupSportSetToAskButton = setupSportSetToAskButton;
const state_1 = require("../../../state");
const ui_1 = require("../../../ui");
// ---------------------------------------------------------------------------
// Ask price + hint text
// ---------------------------------------------------------------------------
/**
 * Get the current ask price for a given side from cached bid/ask, in cents (1–99).
 * cachedBidAsk stores decimal prices in the 0–1 range (e.g. 0.55 = 55¢);
 * multiply by 100 and round to get cents suitable for per-button prices.
 */
function getAskPrice(side) {
    const { cachedBidAsk } = (0, state_1.getState)();
    if (!cachedBidAsk)
        return 50;
    const decimal = side === "yes" ? cachedBidAsk.yesAsk : cachedBidAsk.noAsk;
    // One-sided book: no ask on this side, fall back like a missing cache.
    if (decimal == null)
        return 50;
    return Math.round(decimal * 100);
}
/**
 * Get the effective price hint text for a sport button.
 * Combined mode → shows manual price or ask fallback in ¢.
 * Otherwise      → shows slippage %.
 */
function getSportButtonHintText(meta) {
    const { tradingSettings } = (0, state_1.getState)();
    if ((0, ui_1.isCombinedPricingMode)(tradingSettings)) {
        const price = tradingSettings.baseballButtonManualPrices?.[meta.key] ??
            getAskPrice(meta.side);
        return `${Math.round(price)}¢`;
    }
    const factor = tradingSettings.baseballFactor ?? 0;
    return `${meta.getSlippage(factor)}%`;
}
/**
 * Refresh the hint/label on every given sport button.
 * In combined mode shows manual prices (¢); otherwise shows slippage (%).
 */
function refreshSportButtonHints(buttons) {
    for (const meta of buttons) {
        (0, ui_1.updateBaseballButtonHintText)(meta.hintId, getSportButtonHintText(meta));
    }
}
// ---------------------------------------------------------------------------
// Shared price modal + per-button steppers
// ---------------------------------------------------------------------------
/** Module-level ref tracking which button's price modal is currently open. */
let activePriceMeta = null;
/** Open the per-button price modal pre-set to the button's current price. */
function openPriceModal(meta) {
    activePriceMeta = meta;
    const modal = document.getElementById("bb-price-modal");
    const slider = document.getElementById("bb-price-modal-slider");
    const valueSpan = document.getElementById("bb-price-modal-value");
    const title = document.getElementById("bb-price-modal-title");
    if (!modal || !slider)
        return;
    const state = (0, state_1.getState)();
    const stored = state.tradingSettings.baseballButtonManualPrices?.[meta.key];
    const currentPrice = stored !== undefined ? stored : getAskPrice(meta.side);
    const rounded = Math.max(1, Math.min(99, Math.round(currentPrice)));
    slider.value = rounded.toString();
    if (valueSpan)
        valueSpan.textContent = rounded.toString();
    if (title)
        title.textContent = `Set price: ${meta.key.replace(/_/g, " ")}`;
    modal.classList.remove("hidden");
}
/**
 * Wire up the shared price modal (slider + confirm/cancel).
 * The modal element is shared by every sport's steppers, so wiring is guarded
 * by a data attribute on the element: multiple `setupSportPriceSteppers`
 * calls (one per sport) attach the listeners exactly once.
 */
function setupPriceModal() {
    const modal = document.getElementById("bb-price-modal");
    if (!modal || modal.dataset.sportPricingWired === "true")
        return;
    modal.dataset.sportPricingWired = "true";
    const slider = document.getElementById("bb-price-modal-slider");
    const valueSpan = document.getElementById("bb-price-modal-value");
    const confirmBtn = document.getElementById("bb-price-modal-confirm");
    const cancelBtn = document.getElementById("bb-price-modal-cancel");
    const overlay = document.querySelector(".bb-price-modal-overlay");
    const closeModal = () => {
        modal.classList.add("hidden");
        activePriceMeta = null;
    };
    slider?.addEventListener("input", () => {
        if (valueSpan)
            valueSpan.textContent = slider.value;
    });
    confirmBtn?.addEventListener("click", () => {
        if (!activePriceMeta || !slider) {
            closeModal();
            return;
        }
        const newPrice = parseInt(slider.value, 10);
        const state = (0, state_1.getState)();
        const currentPrices = state.tradingSettings.baseballButtonManualPrices ?? {};
        (0, state_1.updateState)({
            tradingSettings: {
                ...state.tradingSettings,
                baseballButtonManualPrices: { ...currentPrices, [activePriceMeta.key]: newPrice },
            },
        });
        (0, state_1.saveSettingsToStorage)();
        (0, ui_1.updateBaseballButtonHintText)(activePriceMeta.hintId, `${newPrice}¢`);
        closeModal();
    });
    cancelBtn?.addEventListener("click", closeModal);
    overlay?.addEventListener("click", (e) => {
        if (e.target === overlay)
            closeModal();
    });
}
/**
 * Wire up the +/•/− stepper buttons for the given sport buttons.
 * The middle "•" button opens the shared price modal for that button.
 * These buttons are always enabled regardless of the order-button disabled state.
 */
function setupSportPriceSteppers(buttons) {
    setupPriceModal();
    for (const meta of buttons) {
        const incBtn = document.getElementById(meta.incId);
        const decBtn = document.getElementById(meta.decId);
        const setBtn = document.getElementById(meta.setId);
        if (!incBtn || !decBtn || !setBtn)
            continue;
        const adjustPrice = (delta) => {
            const state = (0, state_1.getState)();
            const currentPrices = state.tradingSettings.baseballButtonManualPrices ?? {};
            const stored = currentPrices[meta.key];
            const current = stored !== undefined ? stored : getAskPrice(meta.side);
            const newPrice = Math.max(1, Math.min(99, current + delta));
            (0, state_1.updateState)({
                tradingSettings: {
                    ...state.tradingSettings,
                    baseballButtonManualPrices: { ...currentPrices, [meta.key]: newPrice },
                },
            });
            (0, state_1.saveSettingsToStorage)();
            (0, ui_1.updateBaseballButtonHintText)(meta.hintId, `${newPrice}¢`);
        };
        incBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            adjustPrice(1);
        });
        decBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            adjustPrice(-1);
        });
        setBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            openPriceModal(meta);
        });
    }
}
// ---------------------------------------------------------------------------
// Pin (set-to-ask)
// ---------------------------------------------------------------------------
/**
 * Compute the "pin" price for a sport button: the lowest ask price for that
 * side PLUS the factor-adjusted slippage for that button, clamped to [1, 99].
 * This mirrors the slippage a user would receive if manual price mode were
 * off, anchored to the current market ask.
 */
function getSportPinnedPrice(meta, factor) {
    const ask = getAskPrice(meta.side);
    return Math.max(1, Math.min(99, ask + meta.getSlippage(factor)));
}
/**
 * Wire up a "Set all to Ask" pin button (📌) that populates every given
 * button with the ask price + factor-adjusted slippage for its side, then
 * runs `afterPin` (typically a hint refresh).
 *
 * Pinned prices are MERGED into the existing map so that pinning one sport's
 * buttons never wipes the other sport's stored prices.
 */
function setupSportSetToAskButton(btnElId, buttons, afterPin) {
    const btn = document.getElementById(btnElId);
    if (!btn)
        return;
    btn.addEventListener("click", () => {
        const state = (0, state_1.getState)();
        const factor = state.tradingSettings.baseballFactor ?? 0;
        const currentPrices = state.tradingSettings.baseballButtonManualPrices ?? {};
        const newPrices = {};
        for (const meta of buttons) {
            newPrices[meta.key] = getSportPinnedPrice(meta, factor);
        }
        (0, state_1.updateState)({
            tradingSettings: {
                ...state.tradingSettings,
                baseballButtonManualPrices: { ...currentPrices, ...newPrices },
            },
        });
        (0, state_1.saveSettingsToStorage)();
        afterPin();
    });
}
